/**
 * Tenant → Field Masks (Enterprise · `advanced_rbac` flag)
 *
 * Define which fields are hidden/masked for which roles. Server-side
 * enforcement via mask_response() helper.
 */
import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { useFeatures } from "@/context/FeaturesContext";
import { Plus, Trash2, Lock, Shield, BookOpen } from "lucide-react";
import Modal, { GoldButton, GhostButton, PageHeader, Field, Input, Select } from "@/components/Ui";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

const ROLES = [
    { value: "managing_partner", label: "Managing partner" },
    { value: "advocate", label: "Advocate" },
    { value: "senior_associate", label: "Senior associate" },
    { value: "junior_associate", label: "Junior associate" },
    { value: "paralegal", label: "Paralegal" },
    { value: "clerk", label: "Clerk" },
    { value: "accountant", label: "Accountant" },
];

const MODES = [
    { value: "mask", label: "mask  — replace with •••" },
    { value: "hide", label: "hide  — remove from response (null)" },
];

export default function FieldMasksPage() {
    const { flags } = useFeatures();
    const [data, setData] = useState({ enabled: false, rules: [], supported_collections: [] });
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try { const r = await api.get("/tenant/field-masks"); setData(r.data); }
        catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { if (flags.advanced_rbac) load(); }, [flags.advanced_rbac, load]);

    const tableCtrl = useTableControls(data.rules || [], {
        accessors: {
            enabled: (r) => r.enabled ? "on" : "off",
            roles: (r) => (r.roles || []).join(" "),
        },
    });

    if (!flags.advanced_rbac) return <FlagDisabled/>;

    const save = async () => {
        if (!editing.roles?.length) { toast.error("Pick at least one role"); return; }
        if (!editing.field?.trim()) { toast.error("Field name required"); return; }
        setSaving(true);
        try {
            if (editing.id) {
                const { id, ...patch } = editing;
                await api.patch(`/tenant/field-masks/${id}`, patch); toast.success("Updated");
            } else {
                await api.post("/tenant/field-masks", editing); toast.success("Rule created");
            }
            setEditing(null); load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    const del = async (r) => {
        if (!window.confirm(`Delete rule for ${r.collection}.${r.field}?`)) return;
        try { await api.delete(`/tenant/field-masks/${r.id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    const toggle = async (r) => {
        try { await api.patch(`/tenant/field-masks/${r.id}`, { enabled: !r.enabled }); load(); }
        catch (e) { toast.error(apiError(e)); }
    };

    return (
        <>
            <PageHeader title="Field-level Access Control" subtitle="Hide or mask specific fields for specific roles. Server-side enforced — masked fields never leave the database." action={
                <GoldButton onClick={() => setEditing({
                    collection: data.supported_collections[0] || "clients",
                    field: "", roles: [], mode: "mask", enabled: true, note: "",
                })} data-testid="new-rule"><Plus className="w-4 h-4 inline mr-1"/>New rule</GoldButton>
            }/>

            <ExplainerCard supported={data.supported_collections}/>

            {data.rules.length === 0 ? (
                <div className="border border-dashed border-border py-20 text-center" data-testid="empty-rules">
                    <Shield className="w-8 h-8 mx-auto text-border mb-3"/>
                    <div className="font-serif text-xl mb-1">No masking rules yet</div>
                    <div className="text-sm text-muted-foreground">All roles see all fields. Add a rule to start restricting.</div>
                </div>
            ) : (
                <div className="border border-border overflow-x-auto" data-testid="rules-table">
                    <FilterChip ctrl={tableCtrl} className="m-3"/>
                    <table className="w-full text-sm">
                        <thead className="bg-muted">
                            <tr>
                                <SortableTh field="enabled" ctrl={tableCtrl} className="text-left px-3 py-2">Enabled</SortableTh>
                                <SortableTh field="collection" ctrl={tableCtrl} className="text-left px-3 py-2">Collection</SortableTh>
                                <SortableTh field="field" ctrl={tableCtrl} className="text-left px-3 py-2">Field</SortableTh>
                                <SortableTh field="roles" ctrl={tableCtrl} className="text-left px-3 py-2">Roles</SortableTh>
                                <SortableTh field="mode" ctrl={tableCtrl} className="text-left px-3 py-2">Mode</SortableTh>
                                <SortableTh field="note" ctrl={tableCtrl} className="text-left px-3 py-2">Note</SortableTh>
                                <th className="text-left px-3 py-2 text-xs uppercase tracking-wider font-medium"></th>
                            </tr>
                        </thead>
                        <tbody>{tableCtrl.rows.map(r => (
                            <tr key={r.id} className="border-t border-border" data-testid={`rule-${r.id}`}>
                                <td className="px-3 py-2">
                                    <button onClick={() => toggle(r)} data-testid={`toggle-${r.id}`}
                                        className={`text-[10px] px-2 py-1 uppercase tracking-[0.1em] ${r.enabled ? "bg-emerald-950/40 text-emerald-300" : "bg-border text-muted-foreground"}`}>
                                        {r.enabled ? "On" : "Off"}
                                    </button>
                                </td>
                                <td className="px-3 py-2 font-mono text-xs">{r.collection}</td>
                                <td className="px-3 py-2 font-mono text-xs text-primary">{r.field}</td>
                                <td className="px-3 py-2"><div className="flex flex-wrap gap-1">{r.roles.map(role => (
                                    <span key={role} className="text-[10px] font-mono px-2 py-0.5 bg-card border border-border">{role}</span>
                                ))}</div></td>
                                <td className="px-3 py-2 font-mono text-xs">
                                    <span className={r.mode === "hide" ? "text-rose-400" : "text-amber-300"}>{r.mode}</span>
                                </td>
                                <td className="px-3 py-2 text-xs text-muted-foreground max-w-xs truncate">{r.note || "—"}</td>
                                <td className="px-3 py-2"><div className="flex gap-2">
                                    <button onClick={() => setEditing(r)} className="text-[10px] uppercase tracking-[0.1em] text-primary hover:text-amber-300" data-testid={`edit-${r.id}`}>Edit</button>
                                    <button onClick={() => del(r)} className="text-[10px] uppercase tracking-[0.1em] text-rose-400 hover:text-rose-300" data-testid={`delete-${r.id}`}>
                                        <Trash2 className="w-3 h-3 inline"/>
                                    </button>
                                </div></td>
                            </tr>
                        ))}</tbody>
                    </table>
                </div>
            )}

            {editing && (
                <Modal open={true} onClose={() => setEditing(null)} title={editing.id ? "Edit rule" : "New masking rule"} size="lg" testId="rule-modal">
                    <div className="space-y-4">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Collection">
                                <Select options={data.supported_collections.map(c => ({value: c, label: c}))}
                                    value={editing.collection} onChange={e => setEditing({...editing, collection: e.target.value})} name="col"/>
                            </Field>
                            <Field label="Mode">
                                <Select options={MODES} value={editing.mode} onChange={e => setEditing({...editing, mode: e.target.value})} name="mode"/>
                            </Field>
                        </div>
                        <Field label="Field" hint="Dot-path inside a record. Example: phone, email, total, billing.gst_number">
                            <Input value={editing.field} onChange={e => setEditing({...editing, field: e.target.value})}
                                placeholder="phone" data-testid="field-input"/>
                        </Field>
                        <Field label="Apply to these roles">
                            <div className="grid grid-cols-2 gap-1" data-testid="roles-checklist">{ROLES.map(r => (
                                <label key={r.value} className="flex items-center gap-2 text-xs py-1">
                                    <input type="checkbox" checked={editing.roles.includes(r.value)}
                                        onChange={e => {
                                            const next = e.target.checked
                                                ? [...editing.roles, r.value]
                                                : editing.roles.filter(x => x !== r.value);
                                            setEditing({...editing, roles: next});
                                        }} data-testid={`role-${r.value}`}/>
                                    <span>{r.label}</span>
                                </label>
                            ))}</div>
                            <div className="text-[10px] text-muted-foreground mt-1">
                                ⓘ Firm owner & managing partner are NEVER masked (they need full visibility for audit / billing).
                            </div>
                        </Field>
                        <Field label="Note (optional)">
                            <Input value={editing.note || ""} onChange={e => setEditing({...editing, note: e.target.value})}
                                placeholder="e.g. Junior staff don't need to see client phone numbers per office policy" data-testid="note-input"/>
                        </Field>
                        <div className="flex items-center gap-2 pt-2">
                            <input type="checkbox" id="rule-enabled" checked={editing.enabled}
                                onChange={e => setEditing({...editing, enabled: e.target.checked})}/>
                            <label htmlFor="rule-enabled" className="text-xs">Enabled — rule is applied on every response</label>
                        </div>
                        <div className="flex justify-end gap-2 pt-3 border-t border-border">
                            <GhostButton onClick={() => setEditing(null)}>Cancel</GhostButton>
                            <GoldButton onClick={save} disabled={saving} data-testid="save-rule">{saving ? "Saving…" : "Save rule"}</GoldButton>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
}

function ExplainerCard({ supported }) {
    return (
        <div className="border border-primary/30 bg-primary/5 p-5 mb-6" data-testid="explainer">
            <div className="flex items-center gap-2 mb-3"><BookOpen className="w-4 h-4 text-primary"/><div className="text-sm font-medium">How field masking works</div></div>
            <div className="grid md:grid-cols-2 gap-5 text-xs text-muted-foreground">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Modes</div>
                    <ul className="space-y-1">
                        <li><code className="text-amber-300">mask</code> — value is replaced with <code className="text-foreground">•••</code> in API responses. Field still present so UI layout doesn't break.</li>
                        <li><code className="text-rose-400">hide</code> — field is set to <code className="text-foreground">null</code> entirely. Use when the consumer must not even know the field exists.</li>
                    </ul>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Example</div>
                    <pre className="font-mono text-[11px] bg-background border border-border p-2 overflow-x-auto whitespace-pre-wrap">{`{
  "collection": "clients",
  "field":      "phone",
  "roles":      ["clerk", "junior_associate"],
  "mode":       "mask"
}`}</pre>
                    <div className="text-[10px] text-muted-foreground mt-1">Clerks now see <code>"phone": "•••"</code> instead of the real number.</div>
                </div>
            </div>
            <div className="text-[10px] text-muted-foreground mt-3">Supported collections (MVP): {supported.map(s => <code key={s} className="text-primary mr-2">{s}</code>)}. Every rule action audit-logged under <code className="text-primary">field_mask.*</code>.</div>
        </div>
    );
}

function FlagDisabled() {
    return (
        <>
            <PageHeader title="Field-level Access Control"/>
            <div className="border border-border p-8 text-center" data-testid="flag-disabled">
                <Lock className="w-8 h-8 mx-auto text-border mb-3"/>
                <div className="font-serif text-xl mb-2">Advanced RBAC — Enterprise plan</div>
                <div className="text-sm text-muted-foreground mb-5 max-w-md mx-auto">
                    Hide / mask specific fields for specific roles. Enforced server-side so masked data never leaves the database.
                </div>
                <a href="/app/billing/plans" data-testid="upgrade-link"
                    className="inline-block px-4 py-2 border border-primary text-primary hover:bg-primary/10 text-xs uppercase tracking-[0.15em]">
                    Compare plans · Upgrade →
                </a>
            </div>
        </>
    );
}
